import { httpClient } from '@/core/api/client/httpClient'
import { isHttpError } from '@/core/api/types/http.types'
import type {
  AcceptTermsPayload,
  BiometricEstadoData,
  CaptureContactData,
  CaptureContactPayload,
  CompleteProfilePayload,
  ConsultarEstadoPayload,
  GenerarTokenData,
  GenerarTokenPayload,
  RegistrationResult,
  SendVerificationData,
  SendVerificationPayload,
  StartRegistrationData,
  StartRegistrationPayload,
  StartWithBiometryPayload,
  TermsDocumentsData,
  ValidateInversionistData,
  ValidateInversionistPayload,
  RegistrationDocumentTypesData,
  VerifyCodeData,
  VerifyCodePayload,
} from './registration.types'

interface BffApiResponse<T> {
  success: boolean
  data: T | null
  error?: { code: string; message: string } | null
}

function toResult<T>(response: BffApiResponse<T>): RegistrationResult<T> {
  if (response.success && response.data !== null && response.data !== undefined) {
    return { success: true, data: response.data }
  }
  return {
    success: false,
    errorCode: response.error?.code ?? 'INTERNAL_ERROR',
    message: response.error?.message ?? 'Error inesperado',
  }
}

function toErrorResult<T>(error: unknown): RegistrationResult<T> {
  if (isHttpError(error) && error.data) {
    const body = error.data as BffApiResponse<T>
    return {
      success: false,
      errorCode: body.error?.code ?? 'INTERNAL_ERROR',
      message: body.error?.message ?? 'Error inesperado',
    }
  }
  return { success: false, errorCode: 'NETWORK_ERROR', message: 'Sin conexión' }
}

export const registrationService = {
  async getDocumentTypes(): Promise<RegistrationResult<RegistrationDocumentTypesData>> {
    try {
      const data = await httpClient.get<BffApiResponse<RegistrationDocumentTypesData>>(
        '/api/registration/document-types',
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },
  async getTermsDocuments(lang = 'es'): Promise<RegistrationResult<TermsDocumentsData>> {
    try {
      const data = await httpClient.get<BffApiResponse<TermsDocumentsData>>(
        `/api/registration/terms-documents?lang=${lang}`,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async acceptTerms(
    correlationId: string,
    payload: AcceptTermsPayload,
  ): Promise<RegistrationResult<null>> {
    try {
      const data = await httpClient.post<BffApiResponse<null>>(
        `/api/registration/${correlationId}/accept-terms`,
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async startRegistration(
    payload: StartRegistrationPayload,
  ): Promise<RegistrationResult<StartRegistrationData>> {
    try {
      const data = await httpClient.post<BffApiResponse<StartRegistrationData>>(
        '/api/registration/start',
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async validateInversionist(
    correlationId: string,
    payload: ValidateInversionistPayload,
  ): Promise<RegistrationResult<ValidateInversionistData>> {
    try {
      const data = await httpClient.post<BffApiResponse<ValidateInversionistData>>(
        `/api/registration/${correlationId}/validate-inversionist`,
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async generarTokenBiometria(
    payload: GenerarTokenPayload,
  ): Promise<RegistrationResult<GenerarTokenData>> {
    try {
      const data = await httpClient.post<BffApiResponse<GenerarTokenData>>(
        '/api/biometria/token',
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async consultarEstadoBiometria(
    payload: ConsultarEstadoPayload,
  ): Promise<RegistrationResult<BiometricEstadoData>> {
    try {
      const data = await httpClient.post<BffApiResponse<BiometricEstadoData>>(
        '/api/biometria/estado',
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async startWithBiometry(
    correlationId: string,
    payload: StartWithBiometryPayload,
  ): Promise<RegistrationResult<StartRegistrationData>> {
    try {
      const data = await httpClient.post<BffApiResponse<StartRegistrationData>>(
        `/api/registration/${correlationId}/start-with-biometry`,
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async captureContact(
    correlationId: string,
    payload: CaptureContactPayload,
    lang = 'es',
  ): Promise<RegistrationResult<CaptureContactData>> {
    try {
      const data = await httpClient.post<BffApiResponse<CaptureContactData>>(
        `/api/registration/${correlationId}/capture-contact?lang=${lang}`,
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async sendVerification(
    correlationId: string,
    payload: SendVerificationPayload = {},
    lang = 'es',
  ): Promise<RegistrationResult<SendVerificationData>> {
    try {
      const data = await httpClient.post<BffApiResponse<SendVerificationData>>(
        `/api/registration/${correlationId}/send-verification?lang=${lang}`,
        payload,
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async verifyCode(
    correlationId: string,
    payload: VerifyCodePayload,
  ): Promise<RegistrationResult<VerifyCodeData>> {
    try {
      const response = await httpClient.post<BffApiResponse<VerifyCodeData>>(
        `/api/registration/${correlationId}/verify-code`,
        payload,
      )
      if (response.success) {
        return { success: true, data: response.data ?? { authProfileId: null } }
      }
      return {
        success: false,
        errorCode: response.error?.code ?? 'INTERNAL_ERROR',
        message: response.error?.message ?? 'Error inesperado',
      }
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async startWithoutBiometry(
    correlationId: string,
    cedula: string,
  ): Promise<RegistrationResult<null>> {
    try {
      const data = await httpClient.post<BffApiResponse<null>>(
        `/api/registration/${correlationId}/start-without-biometry`,
        { cedula },
      )
      if (data.success) return { success: true, data: null }
      return {
        success: false,
        errorCode: data.error?.code ?? 'INTERNAL_ERROR',
        message: data.error?.message ?? 'Error inesperado',
      }
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async completeProfile(
    correlationId: string,
    payload: CompleteProfilePayload,
    lang = 'es',
  ): Promise<RegistrationResult<null>> {
    try {
      const data = await httpClient.put<BffApiResponse<null>>(
        `/api/registration/${correlationId}/profile?lang=${lang}`,
        payload,
      )
      if (data.success) return { success: true, data: null }
      return {
        success: false,
        errorCode: data.error?.code ?? 'INTERNAL_ERROR',
        message: data.error?.message ?? 'Error inesperado',
      }
    } catch (error) {
      return toErrorResult(error)
    }
  },
}
